import { useState, useEffect } from "react";
import {
  BadgeCheck,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Coins,
  ExternalLink,
  Gift,
  History,
  Map,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Star,
  Timer,
  UserCheck,
  UserRound,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  bangkokWorkDate,
  thaiTimeShort,
  workDuration,
  TabType,
  SystemFeatureFlags,
} from "./types";
import { openAttendanceConfirmation } from "@/lib/attendance-confirmation";
import { useLanguage } from "@/lib/i18n";

interface AttendanceTabProps {
  currentDisplayName: string;
  now: Date;
  status: any;
  today: any;
  canCheckIn: boolean;
  canCheckout: boolean;
  checkIn: any;
  checkout: any;
  idToken: string | null;
  setConfirmAction: (action: any) => void;
  setActiveTab: (tab: TabType) => void;
  myPerformance: any;
  myMonthlySummary?: any;
  performanceComments: Record<number, string>;
  setPerformanceComments: React.Dispatch<
    React.SetStateAction<Record<number, string>>
  >;
  commentPerformance: any;
  featureFlags?: SystemFeatureFlags;
  candidateNames?: string[];
  handleSaveFirstTimeName?: (name: string) => void;
}

export function AttendanceTab({
  currentDisplayName,
  now,
  status,
  today,
  canCheckIn,
  canCheckout,
  checkIn,
  checkout,
  idToken,
  setConfirmAction,
  setActiveTab,
  myPerformance,
  myMonthlySummary,
  performanceComments,
  setPerformanceComments,
  commentPerformance,
  featureFlags,
  candidateNames = [],
  handleSaveFirstTimeName,
}: AttendanceTabProps) {
  const { t, language } = useLanguage();
  const tx = (th: string, my: string) => (language === "my" ? my : th);

  // Modal Dialog states for mobile popups
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [performanceOpen, setPerformanceOpen] = useState(false);
  const [nameSelectOpen, setNameSelectOpen] = useState(false);

  const [selectedCandidate, setSelectedCandidate] = useState("");
  const [liveCoords, setLiveCoords] = useState<{
    lat: number | null;
    lng: number | null;
    accuracy: number | null;
    loading: boolean;
  }>({
    lat: null,
    lng: null,
    accuracy: null,
    loading: true,
  });

  const refreshLocation = () => {
    if (typeof window !== "undefined" && navigator.geolocation) {
      setLiveCoords((prev) => ({ ...prev, loading: true }));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLiveCoords({
            lat: Number(pos.coords.latitude.toFixed(6)),
            lng: Number(pos.coords.longitude.toFixed(6)),
            accuracy: Math.round(pos.coords.accuracy),
            loading: false,
          });
        },
        () => {
          setLiveCoords((prev) => ({ ...prev, loading: false }));
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
      );
    } else {
      setLiveCoords((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    refreshLocation();
  }, []);

  const historyItems = status.data?.history || [];
  const reviews = myPerformance.data?.reviews || [];

  return (
    <div className="employee-module attendance-module reference-attendance space-y-3 pb-4">
      {/* Primary Compact Attendance Hero Card */}
      <Card className="employee-attendance-card reference-attendance-shell glass-panel overflow-visible rounded-2xl shadow-sm border border-[#e5ede7] py-0 gap-0">
        <div className="h-1 bg-gradient-to-r from-[#1b6b44] via-[#248a58] to-[#6ee7b7]" />

        <CardContent className="reference-attendance-content p-3 sm:p-4 space-y-3">
          {/* User Profile Header (Compact) */}
          <div className="reference-profile-card flex items-center justify-between gap-3 pb-2 border-b border-[#e7ede8]">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="reference-profile-avatar flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#1b6b44] to-[#2e8b57] text-white shadow-2xs">
                <img src="/demo-mascot-attendance.png" alt="" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="reference-active-badge text-[10px] font-black tracking-wide text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200 inline-flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {t.activeEmployeeTitle}
                  </span>
                </div>
                <h2 className="reference-profile-name truncate text-sm sm:text-base font-black text-slate-900 leading-tight">
                  {currentDisplayName || (
                    <button
                      type="button"
                      onClick={() => setNameSelectOpen(true)}
                      className="text-amber-800 underline font-bold"
                    >
                      {t.selectNamePlaceholder}
                    </button>
                  )}
                </h2>
                <p className="reference-profile-department truncate text-xs font-semibold text-slate-500">
                  {status.data?.employee?.department || status.data?.employee?.employeeCode || tx("พนักงาน", "ဝန်ထမ်း")}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 flex-col items-end gap-0.5">
              {today ? (
                today.checkedOutAt ? (
                  <Badge className="bg-[#ebf6ee] text-[#1b6b44] hover:bg-[#ebf6ee] px-2 py-0.5 text-[10px] sm:text-xs font-black rounded-full border border-[#cbe4d2]">
                    <CheckCircle2 size={12} className="mr-1 text-[#1b6b44]" />
                    {t.statusDone}
                  </Badge>
                ) : (
                  <Badge className="bg-[#ebf6ee] text-[#1b6b44] hover:bg-[#ebf6ee] px-2 py-0.5 text-[10px] sm:text-xs font-black rounded-full border border-[#cbe4d2]">
                    <Timer size={12} className="mr-1 text-[#1b6b44]" />
                    {t.statusWorking}
                  </Badge>
                )
              ) : (
                <Badge className="bg-amber-50 text-amber-800 hover:bg-amber-50 px-2 py-0.5 text-[10px] sm:text-xs font-black rounded-full border border-amber-200">
                  <Clock3 size={12} className="mr-1 text-amber-600" />
                  {t.statusNotStarted}
                </Badge>
              )}
              <span className="reference-profile-date inline-flex items-center gap-1 text-[9px] font-semibold text-slate-500">
                <CalendarDays size={13} />
                {status.data?.workDate || bangkokWorkDate(now)}
              </span>
            </div>
          </div>

          {/* Quick Notice if employee name is not set */}
          {!currentDisplayName && (
            <div
              onClick={() => setNameSelectOpen(true)}
              className="cursor-pointer rounded-xl bg-amber-50/90 border border-amber-300 p-2 text-xs flex items-center justify-between text-amber-900 transition hover:bg-amber-100"
            >
              <div className="flex items-center gap-1.5">
                <UserCheck size={14} className="text-amber-700" />
                <span className="font-bold text-[11px] sm:text-xs">
                  {tx("แตะที่นี่เพื่อลงทะเบียนชื่อพนักงานของคุณ", "ဝန်ထမ်းအမည် ရွေးချယ်ရန် ဤနေရာကို နှိပ်ပါ")}
                </span>
              </div>
              <ChevronRight size={14} className="text-amber-700" />
            </div>
          )}

          {/* CleanNest Signature Lush Green Clock Bar (Compact Height) */}
          <div className="employee-clock-card reference-clock-card rounded-xl bg-gradient-to-br from-[#165636] via-[#1b6b44] to-[#257e53] py-2 px-3 text-white text-center shadow-xs relative overflow-hidden border border-white/15 flex items-center justify-between">
            <div className="text-left">
              <span className="text-[9px] uppercase font-bold tracking-wider text-emerald-200 inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-ping" />
                {t.thailandStandardTime}
              </span>
              <p className="text-[11px] font-medium text-emerald-100/90 leading-tight">
                {now.toLocaleDateString(language === "my" ? "my-MM" : "th-TH", {
                  timeZone: "Asia/Bangkok",
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              </p>
            </div>

            <div className="reference-clock-time text-2xl sm:text-3xl font-black font-numeric tabular-nums tracking-tight text-white leading-none drop-shadow-sm">
              {now.toLocaleTimeString("th-TH", {
                timeZone: "Asia/Bangkok",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              })}
            </div>
          </div>

          {/* Automatic Location Tracker Banner (Compact Pill) */}
          <div className="reference-location-card flex items-center justify-between rounded-lg bg-emerald-50/80 border border-emerald-200/90 px-2.5 py-1 text-[11px] text-emerald-950">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="reference-location-icon"><MapPin size={22} /></span>
              <span className="font-extrabold shrink-0 text-slate-800 text-[10px] sm:text-[11px]">
                {t.autoLocationLabel}
              </span>
              {liveCoords.loading ? (
                <span className="text-slate-500 flex items-center gap-1 truncate text-[10px]">
                  <RefreshCw size={11} className="animate-spin" /> {t.locationDetecting}
                </span>
              ) : liveCoords.lat != null ? (
                <span className="font-numeric font-bold text-emerald-900 truncate text-[10px] sm:text-[11px]">
                  {liveCoords.lat.toFixed(4)}, {liveCoords.lng?.toFixed(4)} (~{Math.round(liveCoords.accuracy ?? 0)}ม.)
                </span>
              ) : (
                <span className="text-emerald-800 font-bold truncate text-[10px]">
                  {t.locationReady}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={refreshLocation}
              title={t.locationRefreshTooltip}
              className="p-0.5 rounded hover:bg-emerald-100 text-emerald-800 transition shrink-0 ml-1"
            >
              <RefreshCw size={12} className={liveCoords.loading ? "animate-spin" : ""} />
            </button>
          </div>

          {/* Check-In / Check-Out Action Buttons (Compact Height) */}
          <div className="reference-attendance-actions grid grid-cols-2 gap-2">
            <Button
              className={`reference-check-button h-11 sm:h-12 rounded-xl text-xs sm:text-sm font-black transition-all flex items-center justify-center gap-1.5 p-1.5 shadow-xs ${
                canCheckIn
                  ? "btn-primary-teal active:scale-95 cursor-pointer shadow-[#1b6b44]/25 ring-2 ring-[#1b6b44]/20"
                  : "bg-slate-100 text-slate-400 hover:bg-slate-100 cursor-not-allowed border border-slate-200"
              }`}
              disabled={!canCheckIn}
              onClick={() => {
                if (!idToken && window.liff && !window.liff.isLoggedIn()) {
                  window.liff.login();
                  return;
                }
                setConfirmAction(
                  openAttendanceConfirmation("checkin", canCheckIn)
                );
              }}
            >
              {checkIn.isPending ? (
                <RefreshCw className="animate-spin" size={16} />
              ) : (
                <Check size={16} strokeWidth={3} />
              )}
              <span>{today ? t.btnCheckInDone : t.btnCheckIn}</span>
            </Button>

            <Button
              className={`reference-check-button h-11 sm:h-12 rounded-xl text-xs sm:text-sm font-black transition-all flex items-center justify-center gap-1.5 p-1.5 shadow-xs ${
                canCheckout
                  ? "btn-primary-coral active:scale-95 cursor-pointer shadow-amber-800/25 ring-2 ring-amber-600/20"
                  : "bg-slate-100 text-slate-400 hover:bg-slate-100 cursor-not-allowed border border-slate-200"
              }`}
              disabled={!canCheckout}
              onClick={() => {
                if (!idToken && window.liff && !window.liff.isLoggedIn()) {
                  window.liff.login();
                  return;
                }
                setConfirmAction(
                  openAttendanceConfirmation("checkout", canCheckout)
                );
              }}
            >
              {checkout.isPending ? (
                <RefreshCw className="animate-spin" size={16} />
              ) : (
                <Clock3 size={16} strokeWidth={2.5} />
              )}
              <span>
                {today?.checkedOutAt ? t.btnCheckOutDone : t.btnCheckOut}
              </span>
            </Button>
          </div>

          {/* Today's Active Attendance Strip (When Checked In) */}
          {today && (
            <div className="rounded-xl bg-[#ebf6ee] border border-[#cbe4d2] p-2 text-[11px] text-[#134e31] space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 font-bold">
                  <BadgeCheck size={14} className="text-[#1b6b44] shrink-0" />
                  <span>
                    {t.checkInAtTime}:{" "}
                    <span className="font-numeric text-[#1b6b44] font-black">
                      {thaiTimeShort(today.checkedInAt)} น.
                    </span>
                    {today.checkedOutAt && (
                      <>
                        {" "}· {t.checkOutAtTime}:{" "}
                        <span className="font-numeric text-[#1b6b44] font-black">
                          {thaiTimeShort(today.checkedOutAt)} น.
                        </span>
                      </>
                    )}
                  </span>
                </div>
                {today.checkInLat != null && today.checkInLng != null && (
                  <a
                    href={`https://www.google.com/maps?q=${today.checkInLat},${today.checkInLng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-numeric font-black text-emerald-800 underline flex items-center gap-0.5 hover:text-emerald-950 text-[10px]"
                  >
                    <MapPin size={11} /> {tx("แผนที่", "မြေပုံ")}
                  </a>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QUICK ACTION GRID (BUTTONS THAT OPEN POPUP MODALS) */}
      <div className="reference-quick-grid grid grid-cols-2 gap-3">
        {/* Button 1: Salary Advance */}
        {featureFlags?.enableSalaryAdvance !== false && (
          <button
            type="button"
            onClick={() => {
              window.history.pushState({}, "", "/?module=salary-advance");
              setActiveTab("salary-advance");
            }}
            className="reference-action-card reference-action-advance p-2.5 rounded-xl border border-[#cbe4d2] bg-gradient-to-br from-emerald-50/70 to-white hover:border-[#1b6b44]/60 text-left transition shadow-2xs group flex items-center justify-between"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-[#ebf6ee] text-[#1b6b44] flex items-center justify-center font-bold shrink-0">
                <Coins size={16} />
              </div>
              <div className="min-w-0">
                <h4 className="font-black text-xs text-slate-900 group-hover:text-[#1b6b44] truncate">
                  {t.quickAdvanceTitle}
                </h4>
                <p className="text-[10px] text-slate-500 font-medium truncate">
                  {tx("เบิกตามวันทำงาน", "ကြိုတင်ငွေထုတ်")}
                </p>
              </div>
            </div>
            <ChevronRight size={14} className="text-slate-400 group-hover:text-[#1b6b44] shrink-0" />
          </button>
        )}

        {/* Button 2: Welfare */}
        <button
          type="button"
          onClick={() => {
            window.history.pushState({}, "", "/?module=welfare");
            setActiveTab("welfare");
          }}
          className="reference-action-card reference-action-welfare p-2.5 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-white hover:border-amber-400 text-left transition shadow-2xs group flex items-center justify-between"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center font-bold shrink-0">
              <Gift size={16} />
            </div>
            <div className="min-w-0">
              <h4 className="font-black text-xs text-slate-900 group-hover:text-amber-800 truncate">
                {tx("สวัสดิการพนักงาน", "သက်သာချောင်ချိရေး")}
              </h4>
              <p className="text-[10px] text-slate-500 font-medium truncate">
                {tx("ขอเบิกสวัสดิการ", "ထောက်ပံ့ကြေး")}
              </p>
            </div>
          </div>
          <ChevronRight size={14} className="text-slate-400 group-hover:text-amber-700 shrink-0" />
        </button>

        {/* Button 3: Recent History (POPUP) */}
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          className="reference-action-card reference-action-history p-2.5 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50/70 to-white hover:border-blue-400 text-left transition shadow-2xs group flex items-center justify-between"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-blue-100 text-blue-800 flex items-center justify-center font-bold shrink-0">
              <History size={16} />
            </div>
            <div className="min-w-0">
              <h4 className="font-black text-xs text-slate-900 group-hover:text-blue-800 truncate">
                {tx("ประวัติลงเวลา", "အလုပ်ချိန်မှတ်တမ်း")}
              </h4>
              <p className="text-[10px] text-slate-500 font-medium truncate">
                {tx(`${historyItems.length} รายการล่าสุด`, "စစ်ဆေးရန် နှိပ်ပါ")}
              </p>
            </div>
          </div>
          <ChevronRight size={14} className="text-slate-400 group-hover:text-blue-700 shrink-0" />
        </button>

        {/* Button 4: Work Rules (POPUP) */}
        <button
          type="button"
          onClick={() => setRulesOpen(true)}
          className="reference-action-card reference-action-rules p-2.5 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white hover:border-slate-400 text-left transition shadow-2xs group flex items-center justify-between"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center font-bold shrink-0">
              <ShieldCheck size={16} />
            </div>
            <div className="min-w-0">
              <h4 className="font-black text-xs text-slate-900 group-hover:text-slate-800 truncate">
                {tx("กฎระเบียบ & OT", "စည်းကမ်းနှင့် OT")}
              </h4>
              <p className="text-[10px] text-slate-500 font-medium truncate">
                {tx("เวลาทำงานและเงื่อนไข", "စည်းမျဉ်းများ")}
              </p>
            </div>
          </div>
          <ChevronRight size={14} className="text-slate-400 group-hover:text-slate-700 shrink-0" />
        </button>

        {/* Optional Button 5: Performance Reviews (POPUP) */}
        {featureFlags?.enablePerformanceReview !== false && reviews.length > 0 && (
          <button
            type="button"
            onClick={() => setPerformanceOpen(true)}
            className="col-span-2 p-2.5 rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/70 to-white hover:border-violet-400 text-left transition shadow-2xs group flex items-center justify-between"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-violet-100 text-violet-800 flex items-center justify-center font-bold shrink-0">
                <Star size={16} />
              </div>
              <div className="min-w-0">
                <h4 className="font-black text-xs text-slate-900 group-hover:text-violet-800 truncate">
                  {tx("ผลการประเมินการปฏิบัติงาน", "စွမ်းဆောင်ရည် အကဲဖြတ်မှု")}
                </h4>
                <p className="text-[10px] text-slate-500 font-medium truncate">
                  {tx(`คะแนนรอบ ${reviews[0].reviewPeriod} (${reviews[0].totalScore}/5)`, "အသေးစိတ်ကြည့်မည်")}
                </p>
              </div>
            </div>
            <ChevronRight size={14} className="text-slate-400 group-hover:text-violet-700 shrink-0" />
          </button>
        )}
      </div>

      <div className="reference-detail-stack space-y-3">
        <div className="reference-current-location">
          <div className="reference-detail-icon reference-detail-icon-blue">
            <MapPin size={24} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="reference-detail-label">{tx("ตำแหน่งปัจจุบัน", "လက်ရှိတည်နေရာ")}</p>
            <p className="reference-detail-value font-numeric">
              {liveCoords.lat != null
                ? `${liveCoords.lat.toFixed(4)}, ${liveCoords.lng?.toFixed(4)}`
                : tx("กำลังตรวจสอบตำแหน่ง", "တည်နေရာကို စစ်ဆေးနေသည်")}
            </p>
            {liveCoords.accuracy != null && (
              <p className="reference-detail-meta">{tx(`ความแม่นยำประมาณ ${Math.round(liveCoords.accuracy)} ม.`, `တိကျမှု ${Math.round(liveCoords.accuracy)} မီတာခန့်`)}</p>
            )}
          </div>
          {liveCoords.lat != null && liveCoords.lng != null ? (
            <a
              href={`https://www.google.com/maps?q=${liveCoords.lat},${liveCoords.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="reference-map-button"
            >
              <Map size={19} /> {tx("ดูแผนที่", "မြေပုံ")}
            </a>
          ) : (
            <button type="button" className="reference-map-button" onClick={refreshLocation}>
              <RefreshCw size={18} className={liveCoords.loading ? "animate-spin" : ""} />
              {tx("ตรวจใหม่", "ပြန်စစ်မည်")}
            </button>
          )}
        </div>

        <div className="reference-summary-card">
          <div className="reference-summary-item">
            <div className="reference-detail-icon reference-detail-icon-green">
              <CheckCircle2 size={22} />
            </div>
            <div>
              <p className="reference-detail-label">{tx("วันนี้", "ယနေ့")}</p>
              <p className="reference-detail-value">
                {today ? (today.checkedOutAt ? t.statusDone : t.statusWorking) : t.statusNotStarted}
              </p>
              <p className="reference-detail-meta">{today ? "1 " : "0 "}{tx("รายการ", "မှတ်တမ်း")}</p>
            </div>
          </div>
          <div className="reference-summary-divider" />
          <div className="reference-summary-item">
            <div className="reference-detail-icon reference-detail-icon-orange">
              <CalendarDays size={22} />
            </div>
            <div>
              <p className="reference-detail-label">{tx("สถิติเดือนนี้", "ယခုလစာရင်း")}</p>
              <p className="reference-detail-value">
                {myMonthlySummary?.data?.rows?.length ?? 0} {tx("วันทำงาน", "အလုပ်ရက်")}
              </p>
              <p className="reference-detail-meta">{tx("จากแผนทั้งหมด", "အစီအစဉ်မှ")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* POPUP MODAL 1: Attendance Recent History */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md rounded-2xl p-4">
          <DialogHeader className="pb-2 border-b border-slate-100 pr-8">
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="text-sm sm:text-base font-black text-slate-900 flex items-center gap-1.5 min-w-0 truncate">
                <History size={17} className="text-emerald-700 shrink-0" />
                <span className="truncate">{t.recentHistoryTitle}</span>
              </DialogTitle>
              <button
                type="button"
                onClick={() => {
                  setHistoryOpen(false);
                  window.history.pushState({}, "", "/?module=my-stats");
                  setActiveTab("my-pulse");
                }}
                className="text-[11px] font-bold text-[#1b6b44] hover:underline shrink-0 mr-2"
              >
                {t.viewAllStats}
              </button>
            </div>
            <DialogDescription className="text-[11px] text-slate-500">
              {tx("รายการบันทึกเวลาเข้า-ออกงานล่าสุดของคุณ", "လတ်တလော အလုပ်ချိန်မှတ်တမ်း")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[60vh] overflow-y-auto pt-1">
            {historyItems.length === 0 ? (
              <p className="text-center py-6 text-xs text-slate-400 font-bold">
                {t.noRecentHistory}
              </p>
            ) : (
              historyItems.slice(0, 7).map((item: any) => (
                <div
                  key={item.id}
                  className="p-2.5 rounded-xl border border-slate-200 bg-slate-50/60 flex items-center justify-between"
                >
                  <div>
                    <p className="font-bold text-xs text-slate-900">
                      {item.workDate}
                    </p>
                    <p className="text-[11px] font-semibold text-slate-500">
                      {t.checkInAtTime}:{" "}
                      <span className="font-numeric text-slate-800">
                        {thaiTimeShort(item.checkedInAt)} น.
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    {item.checkedOutAt ? (
                      <>
                        <span className="inline-block rounded-full bg-[#ebf6ee] px-2 py-0.5 text-[10px] font-black text-[#1b6b44] border border-[#cbe4d2]">
                          {thaiTimeShort(item.checkedOutAt)} น.
                        </span>
                        <p className="text-[10px] font-semibold text-slate-400 mt-0.5">
                          {workDuration(item.checkedInAt, item.checkedOutAt)}
                        </p>
                      </>
                    ) : (
                      <span className="inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-black text-amber-700 border border-amber-200">
                        {t.waitingCheckOut}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* POPUP MODAL 2: Work Rules and Overtime */}
      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent className="max-w-md rounded-2xl p-4 sm:p-5">
          <DialogHeader className="pb-2 border-b border-slate-100 pr-8">
            <DialogTitle className="text-base font-black text-slate-900 flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-700" />
              {t.workRulesTitle}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {tx("ข้อกำหนดเวลาทำงานและระเบียบบริษัท", "အလုပ်ချိန်နှင့် စည်းမျဉ်းများ")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-2 text-xs text-slate-700 leading-relaxed">
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-950 space-y-1">
              <h5 className="font-black text-xs text-emerald-900">
                {tx("🕒 เวลาเข้า-ออกงานมาตรฐาน", "ပုံမှန် အလုပ်ချိန်")}
              </h5>
              <p className="text-[11px] font-medium">
                {t.workRulesDesc}
              </p>
            </div>

            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
              <h5 className="font-black text-xs text-slate-900">
                {tx("📍 การลงเวลาผ่านพิกัด GPS", "GPS တည်နေရာ")}
              </h5>
              <p className="text-[11px] font-medium text-slate-600">
                {tx(
                  "ระบบจะจับพิกัดจริงอัตโนมัติขณะกดเข้าและออกงาน เพื่อเป็นหลักฐานยืนยันความถูกต้อง",
                  "စနစ်သည် အလုပ်တက်/ဆင်းချိန်တွင် တည်နေရာကို အလိုအလျောက် မှတ်သားပါသည်"
                )}
              </p>
            </div>

            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-950 space-y-1">
              <h5 className="font-black text-xs text-amber-900">
                {tx("💰 การขอเบิกเงินล่วงหน้า (Salary Advance)", "ကြိုတင်ငွေထုတ်ခြင်း")}
              </h5>
              <p className="text-[11px] font-medium">
                {tx(
                  "พนักงานรายวันสามารถขอเบิกเงินล่วงหน้าได้ตามจำนวนวันทำงานจริงในรอบปัจจุบัน (ตัดรอบวันที่ 10 และ 25)",
                  "လက်ရှိ ကာလအတွင်း အမှန်တကယ် လုပ်ကိုင်ခဲ့သော ရက်များအတွက်သာ ကြိုတင်ငွေ ထုတ်ယူနိုင်ပါသည်"
                )}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* POPUP MODAL 3: Performance Reviews */}
      {reviews.length > 0 && (
        <Dialog open={performanceOpen} onOpenChange={setPerformanceOpen}>
          <DialogContent className="max-w-md rounded-2xl p-4">
            <DialogHeader className="pb-1 border-b border-slate-100 pr-8">
              <DialogTitle className="text-base font-black text-slate-900 flex items-center gap-2">
                <Star size={18} className="text-violet-600" />
                {tx("ผลประเมินการปฏิบัติงาน", "စွမ်းဆောင်ရည် အကဲဖြတ်ချက်")}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {tx("คะแนนและข้อเสนอแนะจากผู้บริหาร", "စီမံခန့်ခွဲသူ၏ အကြံပြုချက်များ")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
              {reviews.map((review: any) => (
                <div
                  key={review.id}
                  className="rounded-xl border border-slate-200 bg-white p-3 space-y-2 text-xs"
                >
                  <div className="flex items-center justify-between">
                    <p className="font-black">รอบ {review.reviewPeriod}</p>
                    <Badge className="bg-teal-700 font-bold">
                      {Number(review.totalScore).toFixed(2)} / 5
                    </Badge>
                  </div>

                  <div className="grid grid-cols-4 gap-1.5 text-center text-[10px]">
                    <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100">
                      <p className="text-slate-500 font-bold">คุณภาพ</p>
                      <p className="font-black text-xs text-teal-800">{review.qualityScore}</p>
                    </div>
                    <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100">
                      <p className="text-slate-500 font-bold">ผลงาน</p>
                      <p className="font-black text-xs text-teal-800">{review.productivityScore}</p>
                    </div>
                    <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100">
                      <p className="text-slate-500 font-bold">ทีมเวิร์ก</p>
                      <p className="font-black text-xs text-teal-800">{review.teamworkScore}</p>
                    </div>
                    <div className="bg-slate-50 p-1.5 rounded-lg border border-slate-100">
                      <p className="text-slate-500 font-bold">วินัยเวลา</p>
                      <p className="font-black text-xs text-teal-800">{review.attendanceScore}</p>
                    </div>
                  </div>

                  {review.managerFeedback && (
                    <p className="text-[11px] font-semibold text-slate-700 bg-slate-50 p-2 rounded-lg">
                      ข้อเสนอแนะ: {review.managerFeedback}
                    </p>
                  )}

                  {review.employeeComment ? (
                    <p className="rounded-lg bg-blue-50 p-2 text-[11px] font-bold text-blue-800">
                      ความคิดเห็นของฉัน: {review.employeeComment}
                    </p>
                  ) : (
                    <div className="flex flex-col gap-1.5 pt-1">
                      <textarea
                        value={performanceComments[review.id] ?? ""}
                        onChange={(e) =>
                          setPerformanceComments((current) => ({
                            ...current,
                            [review.id]: e.target.value,
                          }))
                        }
                        placeholder="เขียนความคิดเห็นหรือการรับทราบผลประเมิน"
                        className="rounded-lg border border-slate-200 p-2 text-xs font-medium"
                        rows={2}
                      />
                      <Button
                        disabled={
                          commentPerformance.isPending ||
                          !(performanceComments[review.id] ?? "").trim()
                        }
                        size="sm"
                        onClick={() =>
                          commentPerformance.mutate({
                            idToken: idToken ?? "",
                            id: review.id,
                            comment: (performanceComments[review.id] ?? "").trim(),
                          })
                        }
                        className="bg-blue-700 font-bold text-xs h-7 self-end"
                      >
                        ส่งความคิดเห็น
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* POPUP MODAL 4: Name Selection */}
      <Dialog open={nameSelectOpen} onOpenChange={setNameSelectOpen}>
        <DialogContent className="max-w-md rounded-2xl p-4 sm:p-5">
          <DialogHeader className="pb-1 pr-8">
            <DialogTitle className="text-base font-black text-emerald-950 flex items-center gap-2">
              <UserRound size={18} className="text-emerald-700" />
              {t.onboardingTitle}
            </DialogTitle>
            <DialogDescription className="text-xs text-emerald-800 font-semibold">
              {t.onboardingSub}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-1">
            <div>
              <label className="block text-xs font-black text-slate-700 mb-1">
                {tx("เลือกชื่อของคุณจากรายชื่อในระบบ *", "အမည် ရွေးချယ်ပါ *")}
              </label>
              <select
                value={selectedCandidate}
                onChange={(e) => setSelectedCandidate(e.target.value)}
                className="w-full rounded-xl border-2 border-emerald-300 bg-white px-3 py-2 text-sm font-bold text-slate-900 shadow-2xs focus:border-emerald-600 focus:outline-none"
              >
                <option value="">{t.selectNamePlaceholder}</option>
                {candidateNames?.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <Button
              onClick={() => {
                if (selectedCandidate && handleSaveFirstTimeName) {
                  handleSaveFirstTimeName(selectedCandidate);
                  setNameSelectOpen(false);
                }
              }}
              disabled={!selectedCandidate}
              className="w-full h-10 rounded-xl bg-[#1b6b44] hover:bg-[#155536] text-white font-bold text-sm shadow-xs cursor-pointer"
            >
              <Check className="mr-1.5" size={16} strokeWidth={3} />
              {t.confirmThisName}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
